/**
 * Hub Services — every service taken by the CP's attributed customers.
 *
 * Filters (status group, service type, customer, customer-name search)
 * change `items` and `total` only; the counts never move with them. Service
 * types on offer come from the partner's own services, so the list only
 * holds types that exist. "Expected closure" is
 * the customer-facing TAT (a duration), never a computed date, and
 * `updated_at` is not treated as a completion date.
 */
import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ClipboardList, Search, Send, Star, X } from 'lucide-react';
import { formatDate } from '@finmin/utils';
import {
  AnimatedTabs,
  Button,
  ErrorMessage,
  FancySelect,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@finmin/ui';
import {
  useChannelPartnerServices,
  type ChannelPartnerHubStatus,
  type ChannelPartnerService,
} from '@/api/channel-partners';
import { HUB_STATUSES, HUB_STATUS_LABELS, formatExpectedClosure, formatMoneySaved, toNumber } from '../format';
import { useDebouncedValue } from '../use-debounced-value';
import { HubEmptyState, HubPager, ServiceStatusBadge } from './hub-bits';
import { NotifyCustomerDialog } from './notify-customer-dialog';

const PAGE_SIZE = 20;

type StatusTab = 'all' | ChannelPartnerHubStatus;

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: 'all', label: 'All' },
  ...HUB_STATUSES.map((s) => ({ value: s, label: HUB_STATUS_LABELS[s] })),
];

export function ChannelPartnerHubServices({
  customerId,
  customerName,
  status,
  serviceType,
}: {
  customerId?: string;
  customerName?: string;
  status?: ChannelPartnerHubStatus;
  serviceType?: string;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [notifyFor, setNotifyFor] = React.useState<ChannelPartnerService | null>(null);
  const name = useDebouncedValue(search.trim());

  React.useEffect(() => setPage(1), [name, customerId, status, serviceType]);

  const list = useChannelPartnerServices({
    page,
    page_size: PAGE_SIZE,
    name: customerId ? undefined : name || undefined,
    customer_id: customerId,
    hub_status: status,
    service_type: serviceType,
  });
  const items = list.data?.items ?? [];
  const filtered = Boolean(customerId || status || name || serviceType);

  // Service-type choices: the distinct types among this partner's services.
  const catalog = useChannelPartnerServices({ page: 1, page_size: 100, customer_id: customerId });
  const typeOptions = React.useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of catalog.data?.items ?? []) {
      if (!seen.has(s.service_type)) seen.set(s.service_type, s.service_name);
    }
    if (serviceType && !seen.has(serviceType)) seen.set(serviceType, serviceType);
    return [
      { value: '', label: 'All services' },
      ...[...seen].map(([value, label]) => ({ value, label })),
    ];
  }, [catalog.data, serviceType]);

  function setStatus(next: StatusTab) {
    void navigate({
      to: '/channel-partner/hub/services',
      search: (prev) => ({ ...prev, status: next === 'all' ? undefined : next }),
    });
  }

  function clearCustomer() {
    void navigate({
      to: '/channel-partner/hub/services',
      search: (prev) => ({ ...prev, customer: undefined, customerName: undefined }),
    });
  }

  function setServiceType(next: string) {
    void navigate({
      to: '/channel-partner/hub/services',
      search: (prev) => ({ ...prev, serviceType: next || undefined }),
    });
  }

  function clearFilters() {
    setSearch('');
    void navigate({ to: '/channel-partner/hub/services', search: {} });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">Services</h2>
          <p className="text-sm text-muted-foreground">
            {list.data
              ? `${list.data.total} service${list.data.total === 1 ? '' : 's'}${filtered ? ' match these filters' : ''}`
              : ' '}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <div className="w-full sm:w-52">
            <FancySelect
              options={typeOptions}
              value={serviceType ?? ''}
              onChange={setServiceType}
              ariaLabel="Filter by service"
            />
          </div>
          {!customerId && (
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by customer name"
                aria-label="Search services by customer name"
                className="pl-9"
              />
            </div>
          )}
          {filtered && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-3.5 w-3.5" aria-hidden /> Clear filters
            </Button>
          )}
        </div>
      </div>

      {customerId && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
          <span className="text-muted-foreground">Customer:</span>
          <span className="font-medium text-foreground">{customerName || 'Selected customer'}</span>
          <button
            type="button"
            onClick={clearCustomer}
            className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden /> Show all customers
          </button>
        </div>
      )}

      <AnimatedTabs
        items={STATUS_TABS}
        value={status ?? 'all'}
        onChange={setStatus}
        size="sm"
        accent="amber"
        ariaLabel="Filter by status"
      />

      <ErrorMessage message={list.error?.message ?? null} />

      {list.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <HubEmptyState
          icon={ClipboardList}
          title={filtered ? 'No services match these filters' : 'No services yet'}
          description={
            filtered
              ? 'Try another status or clear the filters.'
              : 'Services appear here once a customer who used your code picks one.'
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-card lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Expected closure</TableHead>
                  <TableHead className="text-right">Money saved</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead className="text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium text-foreground">{s.customer_name || 'Customer'}</TableCell>
                    <TableCell>
                      <p className="font-medium text-foreground">{s.service_name}</p>
                      <p className="text-xs text-muted-foreground">Started {formatDate(s.created_at)}</p>
                    </TableCell>
                    <TableCell>
                      <ServiceStatusBadge service={s} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatExpectedClosure(s) ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneySaved value={s.money_saved} />
                    </TableCell>
                    <TableCell>
                      <Rating rating={s.rating} feedback={s.feedback} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setNotifyFor(s)}>
                        <Send className="h-3.5 w-3.5" aria-hidden /> Send notification
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile / tablet cards */}
          <ul className="space-y-3 lg:hidden">
            {items.map((s) => (
              <li key={s.id} className="rounded-lg border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">{s.service_name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {s.customer_name || 'Customer'} · Started {formatDate(s.created_at)}
                    </p>
                  </div>
                  <ServiceStatusBadge service={s} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <dt className="text-muted-foreground">Expected closure</dt>
                    <dd className="mt-0.5 text-foreground">{formatExpectedClosure(s) ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Money saved</dt>
                    <dd className="mt-0.5">
                      <MoneySaved value={s.money_saved} />
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-muted-foreground">Rating</dt>
                    <dd className="mt-0.5">
                      <Rating rating={s.rating} feedback={s.feedback} />
                    </dd>
                  </div>
                </dl>
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setNotifyFor(s)}>
                  <Send className="h-3.5 w-3.5" aria-hidden /> Send notification
                </Button>
              </li>
            ))}
          </ul>

          <HubPager
            page={page}
            pageSize={PAGE_SIZE}
            total={list.data?.total ?? 0}
            isFetching={list.isFetching}
            onPageChange={setPage}
          />
        </>
      )}

      <NotifyCustomerDialog
        service={notifyFor}
        onOpenChange={(open) => {
          if (!open) setNotifyFor(null);
        }}
      />
    </div>
  );
}

/** `null` → "Not recorded"; a recorded ₹0 still shows as ₹0. */
function MoneySaved({ value }: { value: ChannelPartnerService['money_saved'] }) {
  const recorded = toNumber(value) !== null;
  return (
    <span className={cn('text-sm tabular-nums', recorded ? 'font-medium text-foreground' : 'text-muted-foreground')}>
      {formatMoneySaved(value)}
    </span>
  );
}

function Rating({ rating, feedback }: { rating: number | null; feedback: string | null }) {
  if (rating === null && !feedback) return <span className="text-sm text-muted-foreground">—</span>;
  return (
    <div className="min-w-0 max-w-[16rem]">
      {rating !== null && (
        <span className="inline-flex items-center gap-0.5" role="img" aria-label={`Rated ${rating} out of 5`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={cn('h-3.5 w-3.5', i < rating ? 'fill-current text-amber' : 'text-muted-foreground/40')}
              aria-hidden
            />
          ))}
        </span>
      )}
      {feedback && (
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground" title={feedback}>
          “{feedback}”
        </p>
      )}
    </div>
  );
}
